import { supabase, SupabaseQuery } from '@/utils/supabase';

export type LearnAiOrgRole = 'super_admin' | 'org_admin' | 'instructor' | 'student';

export interface LearnAiCourse {
  id: string;
  organization_id: string;
  title: string;
  description: string | null;
  state: 'draft' | 'processing' | 'review' | 'published' | 'archived';
  published_at: string | null;
  created_at: string;
}

export interface UploadPdfMaterialParams {
  organizationId: string;
  courseId: string;
  file: Blob & { name?: string; type?: string; size?: number };
}

const MAX_PDF_SIZE_BYTES = 50 * 1024 * 1024;
const ALLOWED_PDF_MIME_TYPES = new Set(['application/pdf']);

function sanitizeFileName(fileName: string): string {
  return fileName
    .replace(/[^\p{L}\p{N}._ -]/gu, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
}

function extFromFileName(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx > -1 ? fileName.slice(idx + 1).toLowerCase() : '';
}

async function sha256Hex(input: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', input);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function ensureLearnAiUserProfile() {
  const { error } = await supabase.rpc('ensure_user_profile', {}, { head: false, count: null, schema: 'learn_ai' } as any);
  if (error) throw error;
}

export async function listMemberOrganizations() {
  return await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('organization_members')
        .select('organization_id, role, organizations:organizations(id, name, slug, billing_plan, seat_limit)')
        .eq('is_active', true)
  );
}

export async function createOrganization(name: string, slug: string) {
  const org: any = await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('organizations')
        .insert({ name, slug })
        .select('id, name, slug')
        .single()
  );

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user || !org) throw new Error('Missing authenticated user');

  await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('organization_members')
        .insert({ organization_id: org.id, user_id: user.id, role: 'org_admin' satisfies LearnAiOrgRole })
        .select('id')
        .single()
  );

  await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('billing_accounts')
        .insert({ organization_id: org.id, plan: 'starter', seat_limit: 50, seat_usage: 1 })
        .select('id')
        .single()
  );

  return org as { id: string; name: string; slug: string };
}

export async function createCourse(organizationId: string, title: string, description: string) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Missing authenticated user');

  return await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('courses')
        .insert({ organization_id: organizationId, title, description, created_by: user.id })
        .select('*')
        .single()
  );
}


export async function getCourse(courseId: string) {
  return await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('courses')
        .select('id, organization_id, title, state')
        .eq('id', courseId)
        .single()
  );
}
export async function listCourses(organizationId: string): Promise<LearnAiCourse[] | null> {
  return await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('courses')
        .select('id, organization_id, title, description, state, published_at, created_at')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
  );
}

export async function uploadTextMaterial(params: {
  organizationId: string;
  courseId: string;
  fileName: string;
  rawText: string;
  fileHash: string;
}) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Missing authenticated user');

  const material = await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('course_materials')
        .insert({
          organization_id: params.organizationId,
          course_id: params.courseId,
          file_name: params.fileName,
          file_hash: params.fileHash,
          mime_type: 'text/plain',
          raw_text: params.rawText,
          uploaded_by: user.id,
        })
        .select('id, parse_status')
        .single()
  );

  return material;
}

export async function uploadPdfMaterial(params: UploadPdfMaterialParams) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Missing authenticated user');

  const originalName = sanitizeFileName((params.file.name || `material-${Date.now()}.pdf`).trim() || `material-${Date.now()}.pdf`);
  const mimeType = (params.file.type || 'application/pdf').toLowerCase();
  const fileExtension = extFromFileName(originalName) || 'pdf';
  const fileSizeBytes = Number(params.file.size || 0);

  if (fileExtension !== 'pdf') {
    throw new Error('invalid_file_extension_only_pdf_allowed');
  }
  if (!ALLOWED_PDF_MIME_TYPES.has(mimeType)) {
    throw new Error('invalid_mime_type_only_pdf_allowed');
  }
  if (!Number.isFinite(fileSizeBytes) || fileSizeBytes <= 0) {
    throw new Error('empty_or_invalid_pdf_file');
  }
  if (fileSizeBytes > MAX_PDF_SIZE_BYTES) {
    throw new Error('pdf_file_too_large_max_50mb');
  }

  const fileBuffer = await params.file.arrayBuffer();
  const fileHash = await sha256Hex(fileBuffer);
  const reserve = await SupabaseQuery.execute(
    async () =>
      await supabase.rpc(
        'register_pdf_material_upload',
        {
          p_organization_id: params.organizationId,
          p_course_id: params.courseId,
          p_file_name: originalName,
          p_file_hash: fileHash,
          p_mime_type: mimeType,
          p_file_size_bytes: fileSizeBytes,
          p_file_extension: fileExtension,
          p_uploaded_by: user.id,
        },
        { schema: 'learn_ai' } as any
      )
  );

  if (!reserve) {
    throw new Error('material_reservation_failed');
  }
  if (reserve.ok === false) {
    throw new Error(String(reserve.error || 'material_reservation_failed'));
  }
  if (typeof reserve.should_upload !== 'boolean') {
    throw new Error('material_reservation_invalid_response');
  }

  if (!reserve.should_upload) {
    return {
      id: reserve.material_id,
      parse_status: reserve.parse_status,
      version: reserve.version,
      file_hash: fileHash,
      storage_path: reserve.storage_path,
      dedupe_hit: true,
    };
  }

  const storagePath = `${params.organizationId}/${params.courseId}/${fileHash}/v${reserve.version}-${originalName}`;
  const uploadResult = await supabase.storage.from('learn-ai-materials').upload(storagePath, params.file, {
    upsert: false,
    contentType: mimeType,
  });

  if (uploadResult.error) {
    await supabase
      .schema('learn_ai')
      .from('course_materials')
      .update({
        parse_status: 'parse_failed',
        parse_error: 'storage_upload_failed',
        metadata: { upload_error: uploadResult.error.message || 'storage_upload_failed' },
      })
      .eq('id', reserve.material_id);
    throw uploadResult.error;
  }

  const { data: material, error: materialError } = await supabase
    .schema('learn_ai')
    .from('course_materials')
    .update({
      storage_path: storagePath,
      parse_status: 'uploaded',
      parse_error: null,
      metadata: {
        original_name: originalName,
        uploaded_via: 'web_file_picker',
        uploaded_at: new Date().toISOString(),
      },
    })
    .eq('id', reserve.material_id)
    .select('id, parse_status, version, file_hash, storage_path')
    .single();

  if (materialError) {
    await supabase.storage.from('learn-ai-materials').remove([storagePath]);
    throw materialError;
  }

  return material;
}

export async function ingestMaterial(materialId: string) {
  const { data, error } = await supabase.rpc('ingest_material', { p_material_id: materialId }, { schema: 'learn_ai' } as any);
  if (error) throw error;
  return data;
}

export async function queueMaterialParseJob(materialId: string) {
  return await ingestMaterial(materialId);
}

export async function parsePdfMaterialViaJob(materialId: string) {
  const { data, error } = await supabase.functions.invoke('learn-ai-parse-material', {
    body: { materialId },
  });
  if (error) throw error;
  return data;
}

export async function generateCourseAssets(courseId: string) {
  const { data, error } = await supabase.rpc('generate_course_assets', { p_course_id: courseId }, { schema: 'learn_ai' } as any);
  if (error) throw error;
  return data;
}

export async function listAssets(courseId: string) {
  return await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('generated_assets')
        .select('id, organization_id, asset_type, title, state, reviewed_at, published_at, content')
        .eq('course_id', courseId)
        .order('created_at', { ascending: true })
  );
}

export async function reviewAsset(assetId: string, action: 'approve' | 'hide' | 'regenerate') {
  const { data, error } = await supabase.rpc('review_asset', { p_asset_id: assetId, p_action: action }, { schema: 'learn_ai' } as any);
  if (error) throw error;
  return data;
}

export async function publishCourse(courseId: string) {
  const { data, error } = await supabase.rpc('publish_course', { p_course_id: courseId }, { schema: 'learn_ai' } as any);
  if (error) throw error;
  return data;
}

export async function getCourseAnalytics(organizationId: string) {
  return await SupabaseQuery.execute(
    async () =>
      await supabase
        .schema('learn_ai')
        .from('course_analytics')
        .select('*')
        .eq('organization_id', organizationId)
        .order('active_students', { ascending: false })
  );
}
